import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";

import { useNavigate } from "react-router-dom";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import { customerApiService } from "services/customerApiService";
import DeleteModal from "pages/Calendar/DeleteModal";
// import UpdateCustomerModal from "./UpdateCustomerModal";

const AllCustomers = () => {
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [deletingCustomer, setDeletingCustomer] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("");

  // Fetch customers
  const fetchCustomers = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await customerApiService.getAllCustomers();
      setCustomers(data);
    } catch (error) {
      toast.error("Failed to fetch customers");
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const showDeleteModal = (id) => {
    setSelectedCustomerId(id);
    setDeleteModal(true);
  };

  const handleDeleteCustomer = async () => {
    setDeletingCustomer(true);
    try {
      await customerApiService.deleteCustomer(selectedCustomerId);
      toast.success("Customer deleted successfully");
      setDeleteModal(false);
      fetchCustomers(false);
    } catch (error) {
      toast.error("Failed to delete customer");
      console.error("Error deleting customer:", error);
    } finally {
      setDeletingCustomer(false);
    }
  };

  const handleUpdateCustomer = (customer) => {
    setSelectedCustomer(customer);
    setUpdateModalOpen(true);
  };

  const handleUpdateSuccess = () => {
    fetchCustomers(false);
    toast.success("Customer updated successfully");
  };

  // Filter customers based on search and type
  const filteredCustomers = useMemo(() => {
    let filtered = customers;

    // Apply user type filter
    if (userTypeFilter) {
      if (userTypeFilter === "noRole") {
        filtered = filtered.filter(customer => !customer.role || customer.role === "");
      } else {
        filtered = filtered.filter(customer => customer.role === userTypeFilter);
      }
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (customer) =>
          (customer.id && customer.id.toString().toLowerCase().includes(query)) ||
          (customer.name && customer.name.toLowerCase().includes(query)) ||
          (customer.phone && customer.phone.toString().toLowerCase().includes(query)) ||
          (customer.email && customer.email.toLowerCase().includes(query)) ||
          (customer.address && customer.address.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [customers, userTypeFilter, searchQuery]);

  const getUserTypeBadge = (role) => {
    const types = {
      customer: { color: "primary", text: "Customer" },
      vendor: { color: "info", text: "Vendor" },
      admin: { color: "success", text: "Admin" },
    };
    const config = types[role] || { color: "secondary", text: role || "General" };
    return <span className={`badge bg-${config.color}`}>{config.text}</span>;
  };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            style={{
              cursor: "pointer",
              color: "blue",
              textDecoration: "underline",
            }}
            onClick={() => navigate(`/customer-details/${row.original.id}`)}
            className=""
          >
            #{row.original.id}
          </div>
        ),
      },
      {
        header: "Photo",
        accessorKey: "photo",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => navigate(`/customer-details/${row.original.id}`)}
            className="text-center"
          >
            {row.original.photo ? (
              <img
                src={row.original.photo}
                alt="Avatar"
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: "#f0f0f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                }}
              >
                <i className="fas fa-user text-muted"></i>
              </div>
            )}
          </div>
        ),
      },
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => navigate(`/customer-details/${row.original.id}`)}
            className=""
          >
            {row.original.name || "-"}
          </div>
        ),
      },
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => navigate(`/customer-details/${row.original.id}`)}
          >
            {row.original.email || "-"}
            {/* <small className="text-truncate d-block" style={{ maxWidth: "150px" }}>
            </small> */}
          </div>
        ),
      },
      {
        header: "Phone",
        accessorKey: "phone",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => navigate(`/customer-details/${row.original.id}`)}
            className=""
          >
            {row.original.phone || "-"}
          </div>
        ),
      },
      // {
      //   header: "Password",
      //   accessorKey: "password_hash",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }) => (
      //     <div
      //       style={{ cursor: "pointer" }}
      //       onClick={() => navigate(`/customer-details/${row.original.id}`)}
      //     >
      //       {row.original.password_hash || "-"}

      //     </div>
      //   ),
      // },
      // {
      //   header: "Role",
      //   accessorKey: "role",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }) => getUserTypeBadge(row.original.role),
      // },
      {
        header: "Address",
        accessorKey: "address",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => navigate(`/customer-details/${row.original.id}`)}
          >
            {row.original.address || "-"}
            {/* <small className="d-block text-truncate" style={{ maxWidth: "150px" }}>
            </small> */}
          </div>
        ),
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            {/* <button
              onClick={() => handleUpdateCustomer(row.original)}
              className="btn btn-warning btn-sm"
              title="Edit Customer"
            >
              <i className="fas fa-edit"></i>
            </button> */}
            <button
              className="btn btn-danger btn-sm"
              onClick={() => showDeleteModal(row.original.id)}
              title="Delete Customer"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // Calculate statistics
  const totalCustomers = customers.length;
  const customerTypeCount = customers.filter(customer => customer.role === 'customer').length;
  const vendorTypeCount = customers.filter(customer => customer.role === 'vendor').length;
  const generalTypeCount = customers.filter(customer => !customer.role || customer.role === '').length;

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Customers" breadcrumbItem="All Customers" />

        {/* Statistics Cards */}
        {/* <div className="row mb-4">
          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Total Customers</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{totalCustomers}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-primary rounded-circle">
                        <i className="fas fa-users font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Customers</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{customerTypeCount}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-success rounded-circle">
                        <i className="fas fa-user-tag font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Vendors</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{vendorTypeCount}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-info rounded-circle">
                        <i className="fas fa-store font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">General Users</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{generalTypeCount}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-warning rounded-circle">
                        <i className="fas fa-user font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Filters and Actions */}
        <div className="row mb-3">
          <div className="col-md-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-3 align-items-center">


                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by ID, name, phone, email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && searchQuery.length === 11) {
                        const match = customers.some(customer =>
                          customer.phone === searchQuery
                        );
                        if (!match) {
                          toast.error("Customer not found");
                        }
                      }
                    }}
                    style={{ width: "250px" }}
                  />
                </div>

                <div className="d-flex align-items-center gap-2">
                  {/* <label className="form-label mb-0">User Type:</label> */}
                  <select
                    className="form-select"
                    style={{ width: "auto" }}
                    value={userTypeFilter}
                    onChange={e => setUserTypeFilter(e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="customer">Customer</option>
                    <option value="vendor">Vendor</option>
                    {/* <option value="noRole">General Users</option> */}
                  </select>
                </div>


                {(userTypeFilter || searchQuery) && (
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setUserTypeFilter("");
                      setSearchQuery("");
                    }}
                  >
                    <i className="fas fa-times me-1"></i> Clear Filters
                  </button>
                )}
              </div>

              <div className="d-flex gap-2">
                <button
                  onClick={() => navigate("/create-customer")}
                  className="btn btn-success"
                >
                  <i className="fas fa-plus me-1"></i> Create New Customer
                </button>

                <button
                  className="btn btn-light"
                  onClick={() => fetchCustomers(false)}
                  disabled={refreshing}
                  title="Refresh Customers"
                >
                  <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Customers Table */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* <div className="mb-3">
                  <h5 className="card-title mb-0">
                    Showing {filteredCustomers.length} of {totalCustomers} customers
                  </h5>
                </div> */}

                <TableContainer
                  columns={columns}
                  data={filteredCustomers}
                  isGlobalFilter={false}
                  isPagination={true}
                  pagination="pagination"
                  paginationWrapper="dataTables_paginate paging_simple_numbers"
                  tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                  isCustomPageSize={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteCustomer}
          loader={deletingCustomer}
          onCloseClick={() => setDeleteModal(false)}
          title="Delete Customer"
          message="Are you sure you want to delete this customer? This action cannot be undone."
        />

        {/* {selectedCustomer && (
          <UpdateCustomerModal
            isOpen={updateModalOpen}
            toggle={() => setUpdateModalOpen(false)}
            customer={selectedCustomer}
            onUpdateSuccess={handleUpdateSuccess}
          />
        )} */}
      </div>
    </div>
  );
};

export default AllCustomers;